<template>
  <hs-modal
    :model-value="true"
    title="Manage Promo Code"
    kicker="Promotion"
    description="Update expiration, usage allowance, activation state, or delete this promo code."
    size="lg"
    @update:modelValue="closeModal"
    @close="closeModal"
  >
    <div class="promo-edit">
      <section class="promo-edit__summary">
        <div class="promo-edit__discount">
          <strong>{{ discountLabel }}</strong>
          <span>OFF</span>
        </div>
        <div class="promo-edit__summary-copy">
          <div class="promo-edit__title-row">
            <h3>{{ selectedPromoCode.code }}</h3>
            <hs-badge :tone="selectedPromoCode.is_active ? 'success' : 'neutral'" size="sm">
              {{ selectedPromoCode.is_active ? "Active" : "Inactive" }}
            </hs-badge>
          </div>
          <p>Expires {{ formatDate(selectedPromoCode.expiration_date, false) }}</p>
          <dl>
            <div>
              <dt>Minimum purchase</dt>
              <dd>RF {{ getFormattedValue(selectedPromoCode.minimum_purchase_required) }}</dd>
            </div>
            <div>
              <dt>Usage</dt>
              <dd>{{ selectedPromoCode.usage_count || 0 }} / {{ selectedPromoCode.usage_limit || "∞" }}</dd>
            </div>
          </dl>
        </div>
      </section>

      <section class="promo-edit__state-card">
        <div>
          <h4>{{ selectedPromoCode.is_active ? "Deactivate promo code" : "Activate promo code" }}</h4>
          <p>
            {{ selectedPromoCode.is_active
              ? "Temporarily hide this promo code from checkout."
              : "Make this promo code available at checkout again." }}
          </p>
        </div>
        <hs-button
          :variant="selectedPromoCode.is_active ? 'danger' : 'success'"
          :icon="selectedPromoCode.is_active ? 'pause-circle' : 'play-circle'"
          :loading="isLoading"
          @click="handlePromoCodeActivation"
        >
          {{ selectedPromoCode.is_active ? "Deactivate" : "Activate" }}
        </hs-button>
      </section>

      <section class="promo-edit__form">
        <div class="promo-edit__grid">
          <hs-date-input
            v-model="expirationDate"
            type="datetime-local"
            label="Expiration Date"
            placeholder="Select expiration date"
            :required="true"
          />
          <hs-input
            v-model="usageLimit"
            type="number"
            label="Usage Limit"
            placeholder="Enter usage limit"
            icon="hash"
          />
        </div>

        <div v-if="!selectedPromoCode.is_active" class="promo-edit__notice">
          <vue-feather type="info" size="17" />
          Activate this promo code before updating expiration or usage limits.
        </div>
      </section>
    </div>

    <template #footer>
      <hs-button variant="danger" icon="trash-2" @click="showDeleteConfirm = true">
        Delete
      </hs-button>
      <span class="promo-edit__footer-spacer" />
      <hs-button variant="secondary" @click="closeModal">Cancel</hs-button>
      <hs-button
        variant="primary"
        icon="save"
        :loading="isUpdating"
        :disabled="!selectedPromoCode.is_active"
        @click="handleUpdatePromoCode"
      >
        Save changes
      </hs-button>
    </template>
  </hs-modal>

  <hs-confirm-dialog
    v-model="showDeleteConfirm"
    title="Delete promo code?"
    :message="`This will permanently delete ${selectedPromoCode.code}. This action cannot be undone.`"
    confirm-text="Delete promo code"
    cancel-text="Keep promo code"
    tone="danger"
    :loading="isDeleting"
    @confirm="handleDeletePromoCode"
  />
</template>

<script>
import { toast } from "vue3-toastify";
import { formatNumber } from "@/helper/utils";
import {
  HsBadge,
  HsButton,
  HsConfirmDialog,
  HsDateInput,
  HsInput,
  HsModal,
} from "@/components/ui";

export default {
  name: "UpdatePromoCode",
  components: {
    HsBadge,
    HsButton,
    HsConfirmDialog,
    HsDateInput,
    HsInput,
    HsModal,
  },
  props: {
    selectedPromoCode: {
      type: Object,
      required: true,
    },
  },
  emits: ["close-modal", "promo-code-updated"],
  data() {
    return {
      expirationDate: "",
      usageLimit: "",
      showDeleteConfirm: false,
    };
  },
  computed: {
    selectedStore() {
      return this.$store.getters.getSelectedStore;
    },
    isLoading() {
      return this.$store.getters.isLoadingActivatePromoCode;
    },
    isUpdating() {
      return this.$store.getters.isLoadingUpdatePromoCode;
    },
    isDeleting() {
      return this.$store.getters.isLoadingDeletePromoCode;
    },
    activationType() {
      return this.selectedPromoCode.is_active ? "deactivate" : "activate";
    },
    discountLabel() {
      if (this.selectedPromoCode.discount_percentage) {
        return `${this.getFormattedValue(this.selectedPromoCode.discount_percentage)}%`;
      }
      return `${this.getFormattedValue(this.selectedPromoCode.discount_amount)} RF`;
    },
  },
  watch: {
    selectedPromoCode: {
      immediate: true,
      handler(newPromoCode) {
        if (newPromoCode) {
          this.usageLimit = newPromoCode.usage_limit;
          this.expirationDate = this.formatDateToDatetimeLocal(
            newPromoCode.expiration_date,
          );
        }
      },
    },
  },
  methods: {
    closeModal() {
      this.$emit("close-modal");
    },
    getFormattedValue(value) {
      return formatNumber(value || 0);
    },
    formatDate(dateString, applyTimezone = true, timezoneOffset = 2) {
      const date = new Date(dateString);
      if (applyTimezone) {
        date.setTime(date.getTime() + timezoneOffset * 60 * 60 * 1000);
      }
      return date.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
        timeZone: "UTC",
      });
    },
    formatPayloadDateToSend(inputDateStr) {
      const inputDate = new Date(inputDateStr);
      const twoDigit = (value) => String(value).padStart(2, "0");
      return `${twoDigit(inputDate.getDate())}/${twoDigit(inputDate.getMonth() + 1)}/${inputDate.getFullYear()} ${twoDigit(inputDate.getHours())}:${twoDigit(inputDate.getMinutes())}:${twoDigit(inputDate.getSeconds())}`;
    },
    async handlePromoCodeActivation() {
      try {
        await this.$store.dispatch("activatePromoCode", {
          id: this.selectedPromoCode.id,
          type: this.activationType,
        });
        this.$emit("promo-code-updated");
        toast.success(
          `${this.selectedPromoCode.code} ${this.activationType}d successfully`,
        );
      } catch (error) {
        toast.error(
          error?.response?.data?.error?.message ||
            `An error occurred when ${this.activationType}ing promo code`,
        );
      }
    },
    async handleUpdatePromoCode() {
      if (!this.expirationDate || !this.usageLimit) {
        toast.error("Expiration date and usage limit are required");
        return;
      }

      try {
        await this.$store.dispatch("updatePromoCode", {
          id: this.selectedPromoCode.id,
          data: {
            expiration_date: this.formatPayloadDateToSend(this.expirationDate),
            usage_limit: Number(this.usageLimit),
          },
        });
        this.$emit("promo-code-updated");
        toast.success("Promo code updated successfully");
      } catch (error) {
        toast.error(
          error?.response?.data?.error?.message ||
            "An error occurred when updating promo code",
        );
      }
    },
    async handleDeletePromoCode() {
      try {
        await this.$store.dispatch("deletePromoCode", this.selectedPromoCode.id);
        this.showDeleteConfirm = false;
        this.$emit("promo-code-updated");
        toast.success("Promo code deleted successfully");
      } catch (error) {
        toast.error(
          error?.response?.data?.error?.message ||
            "An error occurred when deleting promo code",
        );
      }
    },
    formatDateToDatetimeLocal(dateString) {
      const date = new Date(dateString);
      const twoDigit = (value) => String(value).padStart(2, "0");
      return `${date.getFullYear()}-${twoDigit(date.getMonth() + 1)}-${twoDigit(date.getDate())}T${twoDigit(date.getHours())}:${twoDigit(date.getMinutes())}`;
    },
  },
};
</script>

<style scoped>
.promo-edit {
  display: grid;
  gap: 1rem;
}

.promo-edit__summary,
.promo-edit__state-card,
.promo-edit__form {
  background: color-mix(in srgb, var(--hs-bg-soft) 68%, transparent);
  border: 1px solid var(--hs-border);
  border-radius: 18px;
  padding: 1rem;
}

.promo-edit__summary {
  align-items: center;
  display: grid;
  gap: 1rem;
  grid-template-columns: auto 1fr;
}

.promo-edit__discount {
  align-items: center;
  background:
    radial-gradient(circle at 85% 0%, rgba(255, 255, 255, 0.32), transparent 34%),
    linear-gradient(135deg, var(--hs-primary), var(--hs-primary-strong));
  border-radius: 18px;
  color: #fff;
  display: grid;
  height: 5rem;
  justify-items: center;
  min-width: 5.6rem;
  padding: 0.8rem;
}

.promo-edit__discount strong {
  font-size: 1rem;
  font-weight: 950;
}

.promo-edit__discount span {
  font-size: 0.7rem;
  font-weight: 950;
}

.promo-edit__title-row,
.promo-edit__state-card,
.promo-edit__footer-spacer {
  align-items: center;
  display: flex;
}

.promo-edit__title-row {
  gap: 0.7rem;
}

.promo-edit h3,
.promo-edit h4 {
  color: var(--hs-text);
  font-weight: 950;
  margin: 0;
}

.promo-edit h3 {
  font-size: 1.15rem;
}

.promo-edit h4 {
  font-size: 0.95rem;
}

.promo-edit p,
.promo-edit dt {
  color: var(--hs-muted);
  font-size: 0.78rem;
  font-weight: 750;
  margin: 0.2rem 0 0;
}

.promo-edit dl {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin: 0.8rem 0 0;
}

.promo-edit dd {
  color: var(--hs-text);
  font-size: 0.9rem;
  font-weight: 950;
  margin: 0.2rem 0 0;
}

.promo-edit__state-card {
  justify-content: space-between;
  gap: 1rem;
}

.promo-edit__grid {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.promo-edit__notice {
  align-items: center;
  background: color-mix(in srgb, #06b6d4 12%, transparent);
  border: 1px solid color-mix(in srgb, #06b6d4 24%, transparent);
  border-radius: 14px;
  color: #0e7490;
  display: flex;
  font-size: 0.82rem;
  font-weight: 800;
  gap: 0.55rem;
  margin-top: 1rem;
  padding: 0.8rem;
}

.promo-edit__footer-spacer {
  flex: 1;
}

@media (max-width: 700px) {
  .promo-edit__summary,
  .promo-edit__grid {
    grid-template-columns: 1fr;
  }

  .promo-edit__state-card {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
